import React from 'react'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	SelectChangeEvent,
} from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import FileIcon from '@mui/icons-material/InsertDriveFile'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import { useIntl } from 'react-intl'
import { truncateFolderName } from './utils/helpers'
import Select from '../formControls/Select'
import { LayoutRow } from '../layout'
import { useMediaLibrary } from './MediaLibraryContext'

const MediaLibraryFolderNavigation = () => {
	const {
		data,
		folders = [],
		selectedFolder,
		handleSelectFolder,
		handleSelectFile,
	} = useMediaLibrary()
	const { formatMessage } = useIntl()

	const allFilesSelected = selectedFolder === null
	return (
		<>
			<LayoutRow className="mb-4 md:hidden">
				<Select
					menuItems={folders.map((folder) => ({
						value: folder,
						label: truncateFolderName(folder, 20),
					}))}
					label={formatMessage({ id: 'global.folder' })}
					selectProps={{
						onChange: (e: SelectChangeEvent<unknown>) => {
							const folder = e.target.value as string
							handleSelectFolder(folder)
						},
						value: selectedFolder ?? '',
					}}
					fullWidth
				/>
			</LayoutRow>
			<div className="hidden md:block">
				<Accordion
					key="all-files"
					expanded={selectedFolder === null}
					disableGutters
					elevation={0}
					square
					sx={{
						'&:before': {
							display: 'none',
						},
					}}
				>
					<AccordionSummary onClick={() => handleSelectFolder(null)}>
						<span className="flex gap-2 text-sm">
							{allFilesSelected ? (
								<FolderOpenIcon fontSize="small" />
							) : (
								<FolderIcon fontSize="small" />
							)}
							<span className="block leading-0">
								All Files ({data.length})
							</span>
						</span>
					</AccordionSummary>
				</Accordion>
				<hr className="border-gray-200 dark:border-gray-700" />
				{folders.map((folder) => {
					const onClickSelectFolder = () => {
						handleSelectFolder(folder)
					}

					const isSelected = selectedFolder === folder
					return (
						<Accordion
							key={folder}
							expanded={isSelected}
							disableGutters
							elevation={0}
							square
							sx={{
								'&:before': {
									display: 'none',
								},
							}}
						>
							<AccordionSummary onClick={onClickSelectFolder}>
								<span className="flex gap-2 text-sm">
									{isSelected ? (
										<FolderOpenIcon fontSize="small" />
									) : (
										<FolderIcon fontSize="small" />
									)}

									<span className="block leading-0">
										{truncateFolderName(folder)} &#40;
										{
											data.filter(
												(media) => media.parentFolder === folder
											).length
										}
										&#41;
									</span>
								</span>
							</AccordionSummary>
							<AccordionDetails>
								<ul className="flex flex-col">
									{data
										.filter((media) => media.parentFolder === folder)
										.map((media) => {
											const onClickSelectMedia = () => {
												handleSelectFile(media)
											}
											return (
												<li
													key={media.id}
													className="ml-4 text-sm text-gray-500 dark:text-gray-400 hover:underline list-none"
												>
													<button
														type="button"
														onClick={onClickSelectMedia}
														className="flex gap-2 text-left py-3"
													>
														<FileIcon fontSize="inherit" />
														<span className="block leading-4">
															{media.name ?? media.fileName}
														</span>
													</button>
												</li>
											)
										})}
								</ul>
							</AccordionDetails>
						</Accordion>
					)
				})}
			</div>
		</>
	)
}

export default MediaLibraryFolderNavigation
